import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { ColumnDef, Row } from '@tanstack/react-table';
import { useUserRoles } from '@anytag/auth';
import { ProposalJobDescriptionBreakDownMode } from '@anytag/graphql';
import { InfluencerAvatar, SpreadButton } from '@anytag/shared-molecules';
import { formatIntNumber, formatNumber, formatPercent } from '@anytag/shared-utils';
import { Anchor, CheckBox, TableCell, SocialIcon, CheckBoxProps } from '@anytag/shared-atoms';
import { useGenerateInfluencerProfileLink, useTableInputCursor } from '@anytag/shared-hooks';
import { ProposeInfluencersFormValues } from '../schemaTypes';
import { RowData, EditableProposeInfluencersFieldKeys } from '../schemaTypes';
import { useProposeInfluencersApi } from './useProposeInfluencersApi';
import { useProposedInfluencersSearch } from './useProposedInfluencersSearch';

interface Props {
  currency: string;
  listSelectedIds?: number[];
  selectButtonProps: CheckBoxProps;
  jdMode?: ProposalJobDescriptionBreakDownMode;
  onRowSelect: (influencerId: number, socialAccountId?: number) => void;
}

export const useProposedInfluencersTable = ({
  jdMode,
  currency,
  onRowSelect,
  selectButtonProps,
  listSelectedIds = []
}: Props) => {
  const { t } = useTranslation();
  const { isAdminStaffTalentAgencyPartner } = useUserRoles();
  const { isViewMode, isEditMode } = useProposedInfluencersSearch();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();
  const { handleInputFocus, handleInputBlur } = useTableInputCursor();
  const { handleRemoveSmAccountFromProposal } = useProposeInfluencersApi();
  const { control, watch, setValue } = useFormContext<ProposeInfluencersFormValues>();

  const values = watch();

  const handleInputChange =
    (row: Row<RowData>, key: EditableProposeInfluencersFieldKeys) =>
    ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
      if (isEditMode) {
        // Make number always positive
        const inputValue = Math.abs(value ? Number(value) : 0);
        setValue(name as keyof ProposeInfluencersFormValues, value, { shouldDirty: true });

        switch (key) {
          case EditableProposeInfluencersFieldKeys.budget:
            setValue(
              `${row.index}-profit`,
              (inputValue - Number(values[`${row.index}-influencerCost`] ?? 0)).toString(),
              { shouldDirty: true }
            );
            break;
          case EditableProposeInfluencersFieldKeys.influencerCost:
            setValue(`${row.index}-profit`, (Number(values[`${row.index}-budget`] ?? 0) - inputValue).toString(), {
              shouldDirty: true
            });
            break;
          default:
            break;
        }
      }
    };

  const columns: ColumnDef<RowData>[] = [
    {
      size: 40,
      accessorKey: 'select',
      meta: { sticky: true },
      header: () => <CheckBox className="w-4" help={t('Select all')} {...selectButtonProps} />,
      cell: ({ row }) => {
        const { influencer, socialAccount } = row.original;

        const isInfluencerSelected = listSelectedIds.includes(influencer.id);

        if (socialAccount) {
          const isAccountSelected = listSelectedIds.includes(socialAccount.id);

          return isEditMode ? (
            <CheckBox
              checked={isAccountSelected}
              onClick={() => onRowSelect(influencer.id, socialAccount.id)}
              help={isAccountSelected ? t('Unselect') : t('Select')}
            />
          ) : (
            <div className="box-border h-auto flex-none" />
          );
        }

        return isViewMode || jdMode === ProposalJobDescriptionBreakDownMode.INFLUENCER_BREAKDOWN ? (
          <CheckBox
            checked={isInfluencerSelected}
            onClick={() => onRowSelect(influencer.id)}
            help={isInfluencerSelected ? t('Unselect') : t('Select')}
          />
        ) : (
          <div className="box-border h-auto flex-none" />
        );
      }
    },
    {
      accessorKey: 'influencer',
      header: t<string>('HeaderColumn.Influencers'),
      meta: { sticky: true },
      cell: ({ row }) => {
        const { influencer, socialAccount } = row.original;

        return socialAccount ? (
          <div className="account-cell">
            <SocialIcon size="24px" type={socialAccount.socialMedia} />
            <Anchor
              label={socialAccount.name}
              className="ml-4 text-sm"
              {...generateProfileLink(influencer.id, {
                filter: {
                  id: socialAccount.id,
                  sm: socialAccount.socialMedia
                }
              })}
            />
            {isViewMode ? (
              <SpreadButton
                options={[
                  {
                    icon: 'delete',
                    label: 'Remove',
                    onClick: () =>
                      handleRemoveSmAccountFromProposal({
                        influencerId: influencer.id,
                        socialAccountId: socialAccount.id,
                        socialAccountMedia: socialAccount.socialMedia
                      })
                  }
                ]}
                className="ml-auto [&_img]:size-auto"
              />
            ) : null}
          </div>
        ) : (
          <TableCell.Link className="influencer-cell" {...generateProfileLink(influencer.id)}>
            <InfluencerAvatar size="32px" src={influencer.avatar} className="rounded-full" asChild />
            <TableCell.Text value={influencer.name} className="ml-2" />
          </TableCell.Link>
        );
      }
    },
    {
      accessorKey: 'followers',
      header: t<string>('HeaderColumn.Followers'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.followersCount)} />
    },
    {
      accessorKey: 'engagementRateByViews',
      header: t<string>('HeaderColumn.Avg ER by views'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.engagementRateByViews, false)} unit="%" />
      )
    },
    {
      accessorKey: 'engagementRateByFollowers',
      header: t<string>('HeaderColumn.Avg ER by followers'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.engagementRateByFollowers, false)} unit="%" />
      )
    },
    {
      accessorKey: 'budget',
      header: t<string>('HeaderColumn.Budget'),
      meta: { align: 'right' },
      cell: ({ row }) =>
        !row.original.isEditable ? (
          <TableCell.Number value={formatNumber(row.original.stats.budget, 0)} unit={t(currency)} />
        ) : (
          <Controller
            control={control}
            name={`${row.index}-budget` satisfies keyof ProposeInfluencersFormValues}
            render={({ field }) => (
              <TableCell.InputNumber
                {...field}
                min={0}
                placeholder="0"
                rightSection={currency}
                className="justify-end"
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onChange={handleInputChange(row, EditableProposeInfluencersFieldKeys.budget)}
              />
            )}
          />
        )
    },
    {
      accessorKey: 'influencerCost',
      header: t<string>('HeaderColumn.Influencer Cost'),
      meta: { align: 'right', hidden: !isAdminStaffTalentAgencyPartner },
      cell: ({ row }) =>
        !row.original.isEditable ? (
          <TableCell.Number value={formatNumber(row.original.stats.influencerCost, 0)} unit={t(currency)} />
        ) : (
          <Controller
            control={control}
            name={`${row.index}-influencerCost` satisfies keyof ProposeInfluencersFormValues}
            render={({ field }) => (
              <TableCell.InputNumber
                {...field}
                min={0}
                placeholder="0"
                rightSection={currency}
                className="justify-end"
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onChange={handleInputChange(row, EditableProposeInfluencersFieldKeys.influencerCost)}
              />
            )}
          />
        )
    },
    {
      accessorKey: 'profit',
      header: t<string>('HeaderColumn.Profit'),
      meta: { align: 'right', hidden: !isAdminStaffTalentAgencyPartner },
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(Number(values[`${row.index}-profit`] ?? 0), 0)} unit={t(currency)} />
      )
    },
    {
      accessorKey: 'engagementProposed',
      header: t<string>('HeaderColumn.Engagement Proposed'),
      meta: { align: 'right', hidden: !isAdminStaffTalentAgencyPartner },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.stats.engagementProposed, 0)} />
    },
    {
      accessorKey: 'engagementPosted',
      header: t<string>('HeaderColumn.Engagement Posted'),
      meta: { align: 'right', hidden: !isAdminStaffTalentAgencyPartner },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.stats.engagementPosted, 0)} />
    },
    {
      accessorKey: 'addedDate',
      header: t<string>('HeaderColumn.List Added Date'),
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.stats.addedDate} />
    }
  ];

  return { columns };
};
